import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminMobileNav from '../../components/admin/AdminMobileNav';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Bug, RefreshCw, CheckCheck, Trash2, Radio, AlertCircle, AlertTriangle, User, Globe } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const SEVERITY_STYLES = {
  error: 'bg-red-50 text-red-600 border-red-200',
  warn: 'bg-amber-50 text-amber-600 border-amber-200',
};

function formatTime(dateStr) {
  const d = new Date(dateStr);
  const diff = Date.now() - d.getTime();
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return d.toLocaleString();
}

function ErrorRow({ err, onResolve }) {
  const [expanded, setExpanded] = useState(false);
  const Icon = err.severity === 'warn' ? AlertTriangle : AlertCircle;
  return (
    <div
      className={`border rounded-xl p-4 bg-white ${err.resolved ? 'opacity-60' : ''}`}
      data-testid={`error-log-item-${err.id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-lg border flex items-center justify-center flex-shrink-0 ${SEVERITY_STYLES[err.severity] || SEVERITY_STYLES.error}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <button
            className="text-left w-full"
            onClick={() => setExpanded(!expanded)}
            data-testid={`error-log-expand-${err.id}`}
          >
            <p className={`text-sm font-medium text-slate-800 ${expanded ? '' : 'line-clamp-2'}`}>{err.message}</p>
          </button>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs text-slate-500">
            <span>{formatTime(err.last_seen_at || err.created_at)}</span>
            {err.count > 1 && <span className="font-semibold text-slate-600">×{err.count}</span>}
            {err.url && (
              <span className="flex items-center gap-1"><Globe className="w-3 h-3" />{err.url}</span>
            )}
            {err.source && <span className="text-slate-400">{err.source}</span>}
            {err.user_role && (
              <span className="flex items-center gap-1"><User className="w-3 h-3" />{err.user_role}</span>
            )}
          </div>
          {expanded && err.stack && (
            <pre className="mt-2 p-3 bg-slate-900 text-slate-300 text-[11px] rounded-lg overflow-x-auto max-h-48 whitespace-pre-wrap">{err.stack}</pre>
          )}
        </div>
        {!err.resolved && (
          <button
            onClick={() => onResolve(err.id)}
            className="text-xs text-emerald-600 hover:text-emerald-700 font-medium flex-shrink-0"
            data-testid={`error-log-resolve-${err.id}`}
          >
            Resolve
          </button>
        )}
      </div>
    </div>
  );
}

export default function ErrorLogs() {
  const { user } = useAuth();
  const [errors, setErrors] = useState([]);
  const [unresolvedCount, setUnresolvedCount] = useState(0);
  const [severity, setSeverity] = useState('all');
  const [showResolved, setShowResolved] = useState('unresolved');
  const [loading, setLoading] = useState(true);
  const [liveConnected, setLiveConnected] = useState(false);
  const wsRef = useRef(null);

  const authHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  }), []);

  const fetchErrors = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (severity !== 'all') params.set('severity', severity);
      if (showResolved === 'unresolved') params.set('resolved', 'false');
      const res = await axios.get(`${API}/admin/errors/frontend?${params}`, authHeaders());
      setErrors(res.data.errors || []);
      setUnresolvedCount(res.data.unresolved_count || 0);
    } catch (e) {
      toast.error('Failed to load error logs');
    } finally {
      setLoading(false);
    }
  }, [severity, showResolved, authHeaders]);

  useEffect(() => { fetchErrors(); }, [fetchErrors]);

  // Live feed via admin WebSocket
  useEffect(() => {
    if (!user?.id) return;
    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/ws/admin/${user.id}`);
    wsRef.current = ws;
    ws.onopen = () => setLiveConnected(true);
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'frontend_error' && data.error) {
          setErrors((prev) => [data.error, ...prev].slice(0, 100));
          setUnresolvedCount((prev) => prev + 1);
        }
      } catch (e) {
        // ignore malformed ws messages
      }
    };
    ws.onclose = () => setLiveConnected(false);
    ws.onerror = () => setLiveConnected(false);
    return () => { ws.close(); wsRef.current = null; };
  }, [user?.id]);

  const resolveOne = async (id) => {
    try {
      await axios.put(`${API}/admin/errors/frontend/${id}/resolve`, {}, authHeaders());
      setErrors((prev) => prev.map((e) => (e.id === id ? { ...e, resolved: true } : e)));
      setUnresolvedCount((prev) => Math.max(0, prev - 1));
    } catch (e) {
      toast.error('Failed to resolve error');
    }
  };

  const resolveAll = async () => {
    try {
      await axios.put(`${API}/admin/errors/frontend/resolve-all`, {}, authHeaders());
      toast.success('All errors marked as resolved');
      fetchErrors();
    } catch (e) {
      toast.error('Failed to resolve errors');
    }
  };

  const clearAll = async () => {
    if (!window.confirm('Delete all error logs? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/errors/frontend`, authHeaders());
      toast.success('Error logs cleared');
      fetchErrors();
    } catch (e) {
      toast.error('Failed to clear error logs');
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <AdminSidebar />
      <AdminMobileNav />

      <main className="flex-1 p-4 md:p-8 md:ml-64 pt-16 md:pt-4" data-testid="admin-error-logs-page">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Poppins] flex items-center gap-2">
              <Bug className="w-6 h-6 text-red-500" />
              Error Logs
              {unresolvedCount > 0 && (
                <span className="ml-1 px-2 py-0.5 bg-red-100 text-red-600 text-xs rounded-full font-bold" data-testid="error-logs-unresolved-badge">
                  {unresolvedCount} unresolved
                </span>
              )}
            </h1>
            <p className="text-slate-500 flex items-center gap-2">
              Live feed of frontend errors
              <span className={`flex items-center gap-1 text-xs font-medium ${liveConnected ? 'text-emerald-600' : 'text-slate-400'}`} data-testid="error-logs-live-status">
                <Radio className="w-3 h-3" /> {liveConnected ? 'Live' : 'Offline'}
              </span>
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Select value={severity} onValueChange={setSeverity}>
              <SelectTrigger className="w-32" data-testid="error-logs-severity-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All levels</SelectItem>
                <SelectItem value="error">Errors</SelectItem>
                <SelectItem value="warn">Warnings</SelectItem>
              </SelectContent>
            </Select>
            <Select value={showResolved} onValueChange={setShowResolved}>
              <SelectTrigger className="w-36" data-testid="error-logs-resolved-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unresolved">Unresolved</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={fetchErrors} data-testid="error-logs-refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={resolveAll} data-testid="error-logs-resolve-all-btn">
              <CheckCheck className="w-4 h-4 mr-1" /> Resolve all
            </Button>
            <Button variant="outline" size="sm" onClick={clearAll} className="text-red-600 hover:text-red-700" data-testid="error-logs-clear-btn">
              <Trash2 className="w-4 h-4 mr-1" /> Clear
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
          </div>
        ) : errors.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border" data-testid="error-logs-empty">
            <Bug className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">No errors logged</p>
            <p className="text-slate-400 text-sm mt-1">Frontend errors will appear here in real time</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="error-logs-list">
            {errors.map((err) => (
              <ErrorRow key={err.id} err={err} onResolve={resolveOne} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
